import React, { useContext, useEffect, useRef, useState } from 'react'
import { ActionButton, ButtonModel, Pager } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'

// types
import { MyPassesPageQuery, MyPassesPageSetQuery, PassesResponse } from '../../../../types/types'

// utils
import { PAGE_LINKS } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'
import { PAST_PASSES_LIMIT_MY_PASSES_PAGE } from '../../../../utils/enums'

// components
import ErrorScreen from '../../../../atoms/ErrorScreen/ErrorScreen'
import Empty from '../../../../atoms/Empty/Empty'
import VisuallyHidden from '../../../../atoms/VisuallyHidden/VisuallyHidden'
import PassCard, { PassCardSkeleton } from '../PassCard/PassCard'

// assets
import EmptyPassesIcon from '../../../../assets/icons/EmptyPassesIcon'

// styles
import * as SC from '../PassCard/PassCardStyles'

// hooks
import useMessages from '../../../../hooks/useMessages'

type Props = {
	pastPassesContainerRef: React.RefObject<HTMLElement>
	query: MyPassesPageQuery
	setQuery: MyPassesPageSetQuery
}

type FetchAnnounceMode = 'initial' | 'loadMore' | 'pageChange'

const PastPasses = (props: Props) => {
	const { formatMessage, locale } = useIntl()
	const { query, setQuery, pastPassesContainerRef } = props
	const { messages } = useMessages()
	const { apiBrowser } = useContext(AppContext)

	const [pastPasses, setPastPasses] = useState<PassesResponse | undefined>()
	const [isLoading, setIsLoading] = useState(true)
	const [isFetchingFromLoadMoreButton, setIsFetchingFromLoadMoreButton] = useState(false)
	const [statusMessage, setStatusMessage] = useState('')
	const [refetchPastPasses, setRefetchPastPasses] = useState(false)
	const mergeWithPreviousPastPasses = useRef(false)
	const fetchAnnounceModeRef = useRef<FetchAnnounceMode>('initial')

	useEffect(() => {
		const fetchPastPasses = async () => {
			const announceMode = fetchAnnounceModeRef.current

			try {
				setIsLoading(true)
				setStatusMessage(messages.Loading)
				const pastPassesResponse = await apiBrowser.b2c.getPasses({
					state: 'NON_ACTIVE',
					page: query.pastPassesPage,
					limit: PAST_PASSES_LIMIT_MY_PASSES_PAGE
				})

				if (mergeWithPreviousPastPasses.current) {
					setPastPasses((prev) =>
						prev
							? {
									...prev,
									passes: [...prev.passes, ...pastPassesResponse.passes],
									pagination: pastPassesResponse.pagination
								}
							: prev
					)
				} else {
					setPastPasses(pastPassesResponse)
				}

				if (!pastPassesResponse.passes.length && query.pastPassesPage === 1) {
					setStatusMessage(messages['You don’t have any past passes'])
				} else if (announceMode === 'loadMore') {
					setStatusMessage(
						formatMessage(
							{ id: '{count, plural, one {# more pass was loaded} other {# more passes were loaded}}' },
							{ count: pastPassesResponse.passes.length }
						)
					)
				} else if (announceMode === 'pageChange') {
					setStatusMessage(
						formatMessage(
							{ id: 'Page {page} of {totalPages}' },
							{ page: pastPassesResponse.pagination.page, totalPages: pastPassesResponse.pagination.totalPages }
						)
					)
					pastPassesContainerRef.current?.focus({ preventScroll: true })
				} else {
					setStatusMessage('')
				}
			} catch {
				setPastPasses(undefined)
				setStatusMessage(messages['Something went wrong. Data was not loaded.'])
			} finally {
				setIsLoading(false)
				setIsFetchingFromLoadMoreButton(false)
				mergeWithPreviousPastPasses.current = false
				fetchAnnounceModeRef.current = 'initial'
			}
		}

		fetchPastPasses()
	}, [apiBrowser, formatMessage, messages, pastPassesContainerRef, query.pastPassesPage, refetchPastPasses])

	const fetchPastPassesFromLoadMoreBtn = () => {
		setIsFetchingFromLoadMoreButton(true)
		fetchAnnounceModeRef.current = 'loadMore'

		// set mergeWithPreviousPastPasses flag to true so the currently showed past passes can be merged with newly loaded past passes
		mergeWithPreviousPastPasses.current = true

		setQuery({
			...query,
			pastPassesPage: query.pastPassesPage + 1
		})
	}

	const handlePastPassesPageChange = (newPage: number) => {
		fetchAnnounceModeRef.current = 'pageChange'
		pastPassesContainerRef.current?.scrollIntoView({ behavior: 'smooth' })
		setQuery({ ...query, pastPassesPage: newPage })
	}

	const handleRetryFetchingPastPasses = () => {
		fetchAnnounceModeRef.current = 'initial'

		if (query.pastPassesPage !== 1) {
			setQuery({ ...query, pastPassesPage: 1 })
		} else {
			setRefetchPastPasses((current) => !current)
		}
	}

	const remainingPastPassesCount = pastPasses ? pastPasses.pagination.totalCount - pastPasses.pagination.page * pastPasses.pagination.limit : 0

	const showMorePastPassesButtonText = formatMessage({ id: 'Show more passes ({remainingPassesCount})' }, { remainingPassesCount: remainingPastPassesCount })

	const isListBusy = isLoading || isFetchingFromLoadMoreButton

	const getContent = () => {
		if (isLoading && !isFetchingFromLoadMoreButton) {
			const skeletonItems = Array.from({ length: 12 }, (_, index) => <PassCardSkeleton key={index} />)

			return <SC.PassesGrid aria-busy={true}>{skeletonItems}</SC.PassesGrid>
		}

		if (typeof pastPasses === 'undefined') {
			return <ErrorScreen button={{ onClick: handleRetryFetchingPastPasses }} />
		}

		if (!pastPasses.passes.length) {
			return <Empty title={messages['You don’t have any past passes']} icon={<EmptyPassesIcon aria-hidden />} />
		}

		return (
			<>
				<SC.PassesGrid aria-busy={isListBusy || undefined}>
					{pastPasses.passes.map((pass) => (
						<PassCard key={pass.id} pass={pass} href={PAGE_LINKS['/salons/passes/:passID'](locale, pass.id)} cardStyle='past' />
					))}
				</SC.PassesGrid>
				{pastPasses.passes.length > 0 && pastPasses.pagination.totalPages > 1 && (
					<SC.BottomNavigation aria-label={messages['Past passes pagination']}>
						{pastPasses.passes.length > 0 && query.pastPassesPage !== pastPasses.pagination.totalPages && (
							<ActionButton
								isLoading={isFetchingFromLoadMoreButton}
								buttonStyle={ButtonModel.Styles.secondary}
								onClick={fetchPastPassesFromLoadMoreBtn}
							>
								{showMorePastPassesButtonText}
							</ActionButton>
						)}

						<SC.PaginationWrapper>
							<Pager currentPage={query.pastPassesPage} lastPage={pastPasses.pagination.totalPages} onPageChange={handlePastPassesPageChange} />
						</SC.PaginationWrapper>
					</SC.BottomNavigation>
				)}
			</>
		)
	}

	return (
		<>
			<VisuallyHidden as='div' role='status' aria-live='polite' aria-atomic='true'>
				{statusMessage}
			</VisuallyHidden>
			{getContent()}
		</>
	)
}

export default PastPasses
